import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { Seo, breadcrumbJsonLd, faqJsonLd, ORG_ID, SITE_URL } from "../components/Seo";
import { ArrowRight, Building2, CalendarDays, Check, MapPin, Navigation, Phone, ShieldCheck } from "lucide-react";
import { PRIMARY, Overline, AuroraLight, Reveal } from "../components/shared/kursusUi";
import { getLocationPage, LOCATION_PAGES } from "../data/locationPages";
import { CONTACT, postalAddress, VOTRA_LOCATIONS } from "../data/votraLocations";
import { COURSE_GROUPS } from "../data/courseGroups";
import { API_TIMEOUT } from "../utils/crawler";
import { API } from "../App";

const formatDate = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("da-DK", { day: "numeric", month: "long", year: "numeric" });
};

const LocationDetailPage = () => {
  const { slug } = useParams();
  const page = getLocationPage(slug);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    if (!page) return;
    axios
      .get(`${API}/locations/${page.slug}/sessions`, { timeout: API_TIMEOUT })
      .then((r) => setSessions(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSessions([]));
  }, [page]);

  if (!page) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: "hsl(210, 20%, 98%)" }}>
        <Seo title="Lokation ikke fundet | Votra" description="Lokationen findes ikke." path={`/lokationer/${slug}`} noindex />
        <Header />
        <div className="flex-1 flex flex-col items-center justify-center py-24 px-4 text-center">
          <h1 className="font-display text-3xl font-medium text-slate-900 mb-3">Lokationen findes ikke</h1>
          <Link to="/lokationer" className="font-semibold hover:underline" style={{ color: PRIMARY }}>
            Se alle Votras lokationer
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const addresses = VOTRA_LOCATIONS.filter((l) => page.locationIds.includes(l.id));
  const courses = COURSE_GROUPS.filter((g) => page.courseSlugs.includes(g.slug));
  const path = `/lokationer/${page.slug}`;

  const placeBlocks = addresses.map((l) => ({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}${path}#${l.id}`,
    name: l.name,
    url: `${SITE_URL}${path}`,
    telephone: CONTACT.phone,
    email: CONTACT.email,
    address: postalAddress(l),
    parentOrganization: { "@id": ORG_ID },
    ...(page.areaServed ? { areaServed: page.areaServed.map((a) => ({ "@type": "Place", name: a })) } : {}),
    ...(page.isExamVenue
      ? { additionalType: "https://schema.org/EducationalOrganization", description: page.summary }
      : {}),
  }));

  // Svarboksen indgår også som første spørgsmål i FAQ-schemaet, så AI-søgemaskiner
  // kan citere det direkte svar uden at skulle læse hele siden.
  const schemaFaq = page.answer
    ? [{ q: page.answer.q, a: page.answer.body.join(" ") }, ...page.faq]
    : page.faq;

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "hsl(210, 20%, 98%)" }}>
      <div className="votra-grain" aria-hidden />
      <Seo
        title={page.metaTitle || `${page.title}, ${addresses.map((a) => a.street).join(" & ")} | Votra`}
        description={page.metaDescription || page.summary.slice(0, 158)}
        path={path}
        jsonLd={[
          breadcrumbJsonLd([
            { name: "Forside", path: "/" },
            { name: "Lokationer", path: "/lokationer" },
            { name: page.title, path },
          ]),
          ...placeBlocks,
          faqJsonLd(schemaFaq, `${SITE_URL}${path}#faq`),
        ]}
      />
      <Header />

      <section className="hero-light relative overflow-hidden" style={{ backgroundColor: "#F4F6FB" }}>
        <AuroraLight />
        <div className="fx-scan"><span /></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10 py-14 md:py-20">
          <div className="max-w-3xl">
            <Reveal i={1}><Overline light>{page.city}</Overline></Reveal>
            <Reveal i={2}>
              <h1 className="font-display text-4xl sm:text-5xl font-medium text-white leading-tight mb-5">
                {page.heading}
              </h1>
            </Reveal>
            <Reveal i={3}>
              <p className="text-base md:text-lg text-slate-300 leading-relaxed">{page.summary}</p>
            </Reveal>
            <Reveal i={4} className="mt-6 flex flex-wrap gap-3">
              {/* Heroen er lys (.hero-light), så badges skal have mørk tekst.
                  Med bg-white/10 og text-white var de usynlige på baggrunden. */}
              {addresses.some((a) => a.hq) && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white/80 text-slate-700 border border-slate-200 backdrop-blur-sm">
                  <Building2 className="h-3.5 w-3.5" /> Hovedkontor
                </span>
              )}
              {page.isExamVenue && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <ShieldCheck className="h-3.5 w-3.5" /> Godkendt prøvested
                </span>
              )}
            </Reveal>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 md:px-6 py-14 md:py-20 flex-1">
        <div className="max-w-5xl mx-auto space-y-10">
          {/* Direkte svar i Q&A-format. H2 er selve spørgsmålet, så ChatGPT Search og
              Perplexity kan citere svaret uden at skulle fortolke en salgstekst. */}
          {page.answer && (
            <Reveal
              className="rounded-3xl border border-slate-200/80 bg-white p-6 sm:p-9 shadow-[0_8px_30px_rgba(15,40,90,0.06)]"
              data-testid="location-answer"
            >
              <Overline>Kort svar</Overline>
              <h2 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mt-1 mb-4">
                {page.answer.q}
              </h2>
              {page.answer.body.map((p, i) => (
                <p key={i} className="text-slate-600 leading-relaxed mb-3 last:mb-0">{p}</p>
              ))}
              <dl className="mt-6 grid gap-x-8 gap-y-3 sm:grid-cols-2 border-t border-slate-100 pt-6">
                {page.answer.facts.map((f) => (
                  <div key={f.label}>
                    <dt className="text-xs uppercase tracking-wider text-slate-400">{f.label}</dt>
                    <dd className="text-[15px] text-slate-800 mt-0.5">{f.value}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>
          )}

          {/* Adresser + kort */}
          {addresses.map((l) => {
            const query = `${l.street}, ${l.postalCode} ${l.city}, Danmark`;
            return (
              <Reveal
                key={l.id}
                className="bg-white rounded-3xl border border-slate-200/80 shadow-[0_8px_30px_rgba(15,40,90,0.06)] overflow-hidden grid md:grid-cols-2"
                data-testid={`location-address-${l.id}`}
              >
                <div className="p-6 sm:p-8 flex flex-col justify-center">
                  <h2 className="font-display text-xl font-medium text-slate-900">{l.name}</h2>
                  <p className="mt-3 flex items-start gap-2 text-slate-600">
                    <MapPin className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: PRIMARY }} />
                    <span>{l.street}<br />{l.postalCode} {l.city}</span>
                  </p>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(query)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-white transition-transform hover:-translate-y-0.5"
                      style={{ backgroundColor: PRIMARY }}
                      data-testid={`location-directions-${l.id}`}
                    >
                      <Navigation className="h-4 w-4" /> Få vejvisning
                    </a>
                    <a
                      href={`tel:${CONTACT.phone}`}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium border border-slate-200 text-slate-700 hover:bg-slate-50"
                    >
                      <Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}
                    </a>
                  </div>
                </div>
                <div className="min-h-[260px] md:min-h-[300px] bg-slate-100">
                  <iframe
                    title={`Kort: ${l.name}`}
                    src={`https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`}
                    className="w-full h-full min-h-[260px]"
                    style={{ border: 0 }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </Reveal>
            );
          })}

          {/* Det foregår her */}
          <Reveal className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid="location-activities">
            <Overline>På adressen</Overline>
            <h2 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mt-1 mb-4">
              Det foregår her
            </h2>
            <ul className="space-y-2.5">
              {page.whatHappensHere.map((w, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-600">
                  <Check className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: PRIMARY }} />
                  <span className="leading-relaxed">{w}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          {/* Praktisk */}
          <Reveal className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid="location-practical">
            <Overline>Praktisk</Overline>
            <h2 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mt-1 mb-4">
              Sådan kommer du hertil
            </h2>
            <dl>
              {page.practical.map((p) => (
                <div key={p.label} className="flex flex-wrap gap-x-3 gap-y-0.5 py-2.5 border-b border-slate-100 last:border-0">
                  <dt className="w-44 shrink-0 text-sm text-slate-500">{p.label}</dt>
                  <dd className="text-[15px] text-slate-700">{p.value}</dd>
                </div>
              ))}
            </dl>
          </Reveal>

          {/* Kurser på lokationen */}
          {courses.length > 0 && (
            <Reveal className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid="location-courses">
              <Overline>Forløb</Overline>
              <h2 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mt-1 mb-4">
                Forløb knyttet til {page.city}
              </h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {courses.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/kurser/${c.slug}`}
                    className="group flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-4 hover:border-[hsl(210,100%,45%)] transition-colors"
                    data-testid={`location-course-${c.slug}`}
                  >
                    <span>
                      <span className="font-semibold text-slate-900">{c.title}</span>
                      <span className="block text-sm text-slate-500 mt-0.5">{c.shortDescription}</span>
                    </span>
                    <ArrowRight className="h-4 w-4 flex-shrink-0 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                ))}
              </div>
            </Reveal>
          )}

          {/* Kommende hold og prøvedatoer på adressen. Listen hentes live, men siden
              gates aldrig på kaldet: fejler det, står henvisningen til kalenderen. */}
          <Reveal className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid="location-sessions">
            <Overline>Datoer</Overline>
            <h2 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mt-1 mb-4">
              Kommende hold i {page.city}
            </h2>
            {sessions.length > 0 ? (
              <ul className="divide-y divide-slate-100 border-t border-slate-100">
                {sessions.map((s) => (
                  <li key={s.id} className="py-4 flex flex-wrap items-center gap-x-5 gap-y-2" data-testid={`location-session-${s.id}`}>
                    <span className="flex items-center gap-2 w-56 shrink-0 font-semibold text-slate-900">
                      <CalendarDays className="h-4 w-4 flex-shrink-0" style={{ color: PRIMARY }} />
                      {formatDate(s.date)}
                    </span>
                    <Link
                      to={`/kurser/${s.courseSlug}`}
                      className="font-medium hover:underline"
                      style={{ color: PRIMARY }}
                      data-testid={`location-session-link-${s.id}`}
                    >
                      {s.name}
                    </Link>
                    <span className="text-sm text-slate-500">
                      {s.examOnly ? "Prøvedato" : "Holdstart"}
                      {s.languageName && s.language !== "da" ? ` · ${s.languageName}` : ""}
                      {s.price ? ` · ${s.price.toLocaleString("da-DK")} kr.` : ""}
                    </span>
                    {s.spotsLeft > 0 && (
                      <span className="text-sm text-emerald-700 bg-emerald-50 rounded-full px-2.5 py-0.5">
                        {s.spotsLeft} pladser tilbage
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-600 leading-relaxed">
                Der er ingen offentliggjorte datoer i {page.city} lige nu. Alle kommende hold og prøvedatoer
                på begge Votras adresser står i kursuskalenderen.
              </p>
            )}
            <Link
              to="/kalender"
              className="mt-5 inline-flex items-center gap-2 font-semibold hover:gap-3 transition-all"
              style={{ color: PRIMARY }}
              data-testid="location-calendar-link"
            >
              Se alle datoer i kursuskalenderen <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>

          {/* FAQ */}
          <Reveal className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid="location-faq">
            <Overline>Spørgsmål og svar</Overline>
            <h2 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mt-1 mb-4">
              Ofte stillede spørgsmål om {page.title}
            </h2>
            <div className="divide-y divide-slate-200 border-t border-slate-200">
              {page.faq.map((f, i) => (
                <div key={i} className="py-5" data-testid={`location-faq-${i}`}>
                  <h3 className="text-base font-semibold text-slate-900 mb-1.5">{f.q}</h3>
                  <p className="text-slate-600 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Krydslinks, så de to lokationssider peger på hinanden og videre ind i sitet */}
          <div className="flex flex-wrap gap-x-6 gap-y-3" data-testid="location-crosslinks">
            {LOCATION_PAGES.filter((l) => l.slug !== page.slug).map((l) => (
              <Link
                key={l.slug}
                to={`/lokationer/${l.slug}`}
                className="inline-flex items-center gap-2 font-semibold hover:underline"
                style={{ color: PRIMARY }}
                data-testid={`location-other-${l.slug}`}
              >
                {l.title}, {l.city} <ArrowRight className="h-4 w-4" />
              </Link>
            ))}
            <Link to="/lokationer" className="inline-flex items-center gap-2 font-semibold hover:underline" style={{ color: PRIMARY }}>
              Alle lokationer <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/adresser" className="inline-flex items-center gap-2 font-semibold hover:underline" style={{ color: PRIMARY }}>
              Alle adresser med kort <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/kontakt" className="inline-flex items-center gap-2 font-semibold hover:underline" style={{ color: PRIMARY }}>
              Kontakt os <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LocationDetailPage;
